import { useEffect, useState } from "react";
import { useUser } from "../pages/auth/context/user-context";
import { getUser } from "../helpers/utils";

// "autumn_" prefix is deliberate: auth-session.ts preserves autumn_* keys
// across logout, so this latch needs no allowlist edit there.
export const minFeeEarlyWarningStorageKey = (userId: string) =>
  `autumn_min_fee_early_warning_dismissed_${userId}`;

/**
 * One-time "good news" popup gate for the min-fee early-warning email
 * (clone of useFirstBookingCongrats).
 *
 * Opens on the user's first visit after the early-warning email was sent -
 * no expiry, it waits for them however long that takes. Show-once-then-gone:
 * any dismissal writes a per-user localStorage latch, so it never re-opens
 * on this browser.
 */
export function useMinFeeEarlyWarning() {
  // UserContext is untyped (see main.tsx's `useUser() as any` idiom):
  // narrow to the one field this hook reads.
  const { onboardingData } = useUser() as {
    onboardingData?: Record<string, unknown>;
  };
  const [open, setOpen] = useState(false);
  const userId = getUser()?.id as string | undefined;

  const sentAtRaw = onboardingData?.minFeeEarlyWarningEmailSentAt as
    | string
    | null
    | undefined;

  useEffect(() => {
    // Fully derived: also CLOSES an already-open popup if the user or
    // onboarding data changes to an ineligible state while mounted.
    const eligible =
      !!userId &&
      !!sentAtRaw &&
      !Number.isNaN(Date.parse(sentAtRaw)) &&
      !localStorage.getItem(minFeeEarlyWarningStorageKey(userId));
    setOpen(eligible);
  }, [userId, sentAtRaw]);

  const dismiss = () => {
    // Close FIRST: every close path of the modal routes through here, so a
    // throwing setItem (quota, restricted storage) must never leave the
    // full-screen dialog stuck open. Worst case the latch is not written
    // and the popup reappears next session, which is the safe failure.
    setOpen(false);
    try {
      if (userId) {
        localStorage.setItem(
          minFeeEarlyWarningStorageKey(userId),
          new Date().toISOString(),
        );
      }
    } catch {
      // Storage unavailable: the popup stays dismissed for this session.
    }
  };

  return { open, dismiss };
}
